import React, { useState } from "react";
import { Modal } from "antd";
import { Check, CheckCircle, ChevronRight, CreditCard, Tag } from "lucide-react";
import SubscribeModulePicker, {
  ProviderBadge,
  getProviderTheme,
} from "../SubscribeModulePicker";
import {
  computeSubscribeMonthlyTotal,
  formatPlanModuleSummary,
  getDefaultOrgPlan,
  getOrgKey,
  getOrgPlanStatus,
} from "../../utils/subscribeModules";
import { NEW_CUSTOMER_BILLING_COMPACT_LINE } from "../../constants/newCustomerBilling";
import "./multi-subscribe-wizard-modal.scss";

import CouponInput from "../CouponInput";

function OrgSidebarItem({ org, plan, isActive, onSelect, onClick }) {
  const provider = org.provider ?? "xero";
  const theme = getProviderTheme(provider);
  const status = getOrgPlanStatus(plan);
  const monthlyTotal = plan.skipped || !plan.accounts
    ? null
    : computeSubscribeMonthlyTotal(plan);
  const orgKey = getOrgKey(org);
  const handleClick = onSelect || onClick;

  return (
    <button
      type="button"
      onClick={() => handleClick?.(orgKey)}
      className={`multi-subscribe-wizard__sidebar-item w-full rounded-lg border px-3 py-2.5 text-left transition-colors ${
        isActive
          ? "border-gray-300 bg-white shadow-sm"
          : "border-transparent hover:bg-white/60"
      }`}
      style={isActive ? { boxShadow: `inset 3px 0 0 ${theme.primary}` } : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900">{org.account}</p>
          <p className="mt-0.5 text-xs text-gray-500">
            {getProviderTheme(provider).label}
          </p>
        </div>
        {status === "included" && (
          <div className="text-right">
            <span className="shrink-0 text-xs font-semibold text-emerald-600">
              ${monthlyTotal.toFixed(2)}
            </span>
            {plan.coupon_discount && (
              <p className="text-[10px] text-emerald-700 font-medium">Coupon</p>
            )}
          </div>
        )}
        {status === "skipped" && (
          <span className="shrink-0 text-xs text-gray-400">Skipped</span>
        )}
      </div>
    </button>
  );
}

function SummaryStep({
  orgs,
  orgPlans,
  paymentCards = [],
  defaultPaymentMethodId = null,
  selectedPaymentMethodId = null,
  onPaymentMethodChange,
  onAddNewCard,
  onApplyCouponToAll,
  onRemoveCouponFromAll,
}) {
  const [batchCouponCode, setBatchCouponCode] = React.useState("");
  const [batchCouponDiscount, setBatchCouponDiscount] = React.useState(null);
  const [redemptionNotice, setRedemptionNotice] = React.useState("");

  const included = orgs.filter((org) => {
    const plan = orgPlans[getOrgKey(org)] ?? orgPlans[org.id];
    return plan && !plan.skipped && plan.accounts;
  });
  const skipped = orgs.filter((org) => {
    const plan = orgPlans[getOrgKey(org)] ?? orgPlans[org.id];
    return !plan || plan.skipped || !plan.accounts;
  });
  const grandTotal = included.reduce(
    (sum, org) =>
      sum +
      computeSubscribeMonthlyTotal(
        orgPlans[getOrgKey(org)] ?? orgPlans[org.id]
      ),
    0
  );
  const chargeImmediatelyCount = included.filter(
    (org) => org.subscribe_will_charge_immediately
  ).length;

  const currentPaymentMethodId =
    selectedPaymentMethodId ||
    (defaultPaymentMethodId &&
    paymentCards.some((c) => c.id === defaultPaymentMethodId)
      ? defaultPaymentMethodId
      : paymentCards.length > 0
      ? paymentCards[0].id
      : null);

  const handleDiscountApplied = (discountInfo) => {
    if (!discountInfo) {
      setBatchCouponDiscount(null);
      setRedemptionNotice("");
      onRemoveCouponFromAll?.();
      return;
    }

    const availableRedemptions =
      discountInfo.max_redemptions != null
        ? Math.max(
            0,
            discountInfo.max_redemptions - (discountInfo.times_redeemed || 0)
          )
        : Infinity;

    if (availableRedemptions >= included.length) {
      setBatchCouponDiscount(discountInfo);
      setRedemptionNotice("");
      onApplyCouponToAll?.(batchCouponCode || discountInfo.code, discountInfo);
    } else {
      const code = batchCouponCode || discountInfo.code || "entered";
      setBatchCouponDiscount(null);
      onRemoveCouponFromAll?.();
      setRedemptionNotice(
        `✖ Promo code ${code} cannot be applied in bulk (Only ${availableRedemptions} redemption${availableRedemptions === 1 ? "" : "s"} available for ${included.length} organizations).`
      );
    }
  };

  return (
    <div>
      <h3 className="text-base sm:text-lg font-semibold text-gray-900">Review your selections</h3>
      <p className="mt-0.5 text-xs sm:text-sm text-gray-600">
        Each organization gets its own subscription and invoice.
      </p>

      {/* Payment Method Selector */}
      <div className="mt-3.5 rounded-xl border border-gray-200 bg-white p-3 sm:p-3.5 shadow-sm">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
            <CreditCard className="h-4 w-4 text-gray-500" />
            Payment Method
          </label>
          {onAddNewCard && (
            <button
              type="button"
              onClick={onAddNewCard}
              className="text-xs font-semibold text-sky-600 hover:text-sky-800 hover:underline"
            >
              + Add New Card
            </button>
          )}
        </div>

        {paymentCards.length > 0 ? (
          <select
            value={currentPaymentMethodId || ""}
            onChange={(e) => {
              if (e.target.value === "ADD_NEW") {
                onAddNewCard?.();
              } else {
                onPaymentMethodChange?.(e.target.value);
              }
            }}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs sm:text-sm text-gray-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          >
            {paymentCards.map((cardItem) => {
              const cardObj = cardItem.card || cardItem;
              const isDefault = cardItem.id === defaultPaymentMethodId;
              const brand = (cardObj.brand || cardObj.display_brand || "Card").toUpperCase();
              const last4 = cardObj.last4 || "";
              const expMonth = cardObj.exp_month
                ? String(cardObj.exp_month).padStart(2, "0")
                : "";
              const expYear = cardObj.exp_year || "";
              const expiryText =
                expMonth && expYear ? ` (Expires ${expMonth}/${expYear})` : "";
              return (
                <option key={cardItem.id} value={cardItem.id}>
                  {brand} •••• {last4}{expiryText}{isDefault ? " — (Default)" : ""}
                </option>
              );
            })}
          </select>
        ) : (
          <div className="flex items-center justify-between rounded-lg border border-dashed border-gray-300 p-2.5 bg-gray-50 text-xs sm:text-sm">
            <span className="text-gray-500 text-xs">No payment card on file</span>
            <button
              type="button"
              onClick={onAddNewCard}
              className="rounded bg-sky-600 px-3 py-1 text-xs font-semibold text-white hover:bg-sky-700 transition"
            >
              Add Card
            </button>
          </div>
        )}
      </div>

      {included.length > 0 && (
        <div className="mt-3.5 rounded-xl border border-gray-200 bg-gray-50/70 p-3 sm:p-3.5">
          <CouponInput
            label={
              included.length > 1
                ? "Have a Coupon? (Apply to All Selected Organizations)"
                : "Have a Coupon?"
            }
            helperText={
              included.length > 1
                ? "Enter a promo code here to apply this discount across all selected organizations at once."
                : null
            }
            value={batchCouponCode}
            onChange={(code) => {
              setBatchCouponCode(code);
              if (!code) {
                setBatchCouponDiscount(null);
                setRedemptionNotice("");
              }
            }}
            appliedDiscount={batchCouponDiscount}
            onDiscountApplied={handleDiscountApplied}
          />

          {batchCouponDiscount ? (
            <div className="mt-2 text-xs space-y-1">
              <div className="flex flex-wrap items-center gap-1.5 text-gray-700">
                <span className="font-semibold text-gray-800">Coupon Duration:</span>
                <span className="rounded bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800">
                  {batchCouponDiscount.duration === "once"
                    ? "First month only (1-time duration)"
                    : batchCouponDiscount.duration === "repeating"
                    ? `${batchCouponDiscount.duration_in_months || ""} Months recurring`
                    : "Forever (all recurring invoices)"}
                </span>
              </div>
              <p className="text-emerald-700 font-medium">
                ✓ {included.length === 1
                  ? "Applied to the selected organization."
                  : `Automatically applied to all ${included.length} selected organizations.`}
              </p>
            </div>
          ) : redemptionNotice ? (
            <div className="mt-2">
              <p className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs font-medium text-red-800">
                {redemptionNotice}
              </p>
            </div>
          ) : null}
        </div>
      )}

      {included.length > 0 ? (
        <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-50 text-left text-[11px] sm:text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3 font-medium">Organization</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3 font-medium">Modules</th>
                <th className="px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-right">Monthly</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {included.map((org) => {
                const plan = orgPlans[getOrgKey(org)] ?? orgPlans[org.id];
                const baseTotal = computeSubscribeMonthlyTotal({
                  ...plan,
                  coupon_discount: null,
                });
                const finalTotal = computeSubscribeMonthlyTotal(plan);

                return (
                  <tr key={getOrgKey(org)}>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3 font-medium text-gray-900">
                      <div>{org.account}</div>
                      {plan.coupon_code && (
                        <div className="mt-0.5 text-xs text-emerald-600 font-medium">
                          Promo: {plan.coupon_code}
                          {plan.coupon_discount?.percent_off != null &&
                            ` (${plan.coupon_discount.percent_off}% off)`}
                          {plan.coupon_discount?.amount_off != null &&
                            ` ($${Number(plan.coupon_discount.amount_off).toFixed(2)} off)`}
                        </div>
                      )}
                    </td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-gray-600">
                      {formatPlanModuleSummary(plan, org.provider ?? "xero", org.country_code)}
                    </td>
                    <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-right">
                      {plan.coupon_discount ? (
                        <div>
                          <span className="text-xs text-gray-400 line-through mr-1.5">
                            ${baseTotal.toFixed(2)}
                          </span>
                          <span className="text-xs sm:text-sm font-bold text-emerald-600">
                            ${finalTotal.toFixed(2)}
                          </span>
                        </div>
                      ) : (
                        <span className="font-semibold text-gray-900">
                          ${finalTotal.toFixed(2)}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={2} className="px-3 sm:px-4 py-2.5 sm:py-3 text-right font-semibold text-gray-900">
                  Total monthly cost
                </td>
                <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-right text-base sm:text-lg font-bold text-gray-900">
                  ${grandTotal.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs sm:text-sm text-amber-900">
          No organizations selected. Go back and enable Account for at least one organization.
        </p>
      )}

      {skipped.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] sm:text-xs font-medium uppercase tracking-wide text-gray-500">Skipped</p>
          <p className="mt-0.5 text-xs sm:text-sm text-gray-600">
            {skipped.map((org) => org.account).join(", ")}
          </p>
        </div>
      )}

      {included.length > 0 && (
        <div className="mt-3.5 rounded-lg border border-sky-200 bg-sky-50 px-3.5 py-2.5 text-xs sm:text-sm text-sky-950">
          {chargeImmediatelyCount === 0 ? (
            <p>{NEW_CUSTOMER_BILLING_COMPACT_LINE}</p>
          ) : chargeImmediatelyCount === included.length ? (
            <p>
              {included.length === 1
                ? "This organization has already used its free trial and will be charged immediately on your chosen payment method, then monthly on autopay."
                : "All selected organizations have already used their free trial and will be charged immediately on your chosen payment method, then monthly on autopay."}
            </p>
          ) : (
            <p>
              {chargeImmediatelyCount} of {included.length} organizations will be
              charged immediately (prior trial used). The remaining{" "}
              {included.length - chargeImmediatelyCount} will start with a 30-day free
              trial before auto-billing on your chosen payment method.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function MultiSubscribeWizardModal({
  open,
  onClose,
  orgs = [],
  orgPlans,
  currentStepIndex,
  showSummary,
  paymentCards = [],
  defaultPaymentMethodId = null,
  selectedPaymentMethodId = null,
  onPaymentMethodChange,
  onAddNewCard,
  onOrgSelect,
  onPlanChange,
  onSkipOrg,
  onSkipAll,
  onIncludeAll,
  onApplyCouponToAll,
  onRemoveCouponFromAll,
  onNext,
  onBack,
  onConfirm,
  confirming = false,
}) {
  const isMulti = orgs.length > 1;
  const currentOrg = showSummary ? null : orgs[currentStepIndex];
  const currentOrgKey = currentOrg ? getOrgKey(currentOrg) : "";
  const provider = currentOrg?.provider ?? "xero";
  const theme = getProviderTheme(provider);
  const isQbo = provider === "qbo";
  const currentPlan = currentOrg
    ? orgPlans[currentOrgKey] ?? orgPlans[currentOrg.id] ?? getDefaultOrgPlan()
    : getDefaultOrgPlan();
  const isLastOrg = currentStepIndex >= orgs.length - 1;
  const includedCount = orgs.filter((org) => {
    const plan = orgPlans[getOrgKey(org)] ?? orgPlans[org.id];
    return plan && !plan.skipped && plan.accounts;
  }).length;

  const currentPaymentMethodId =
    selectedPaymentMethodId ||
    (defaultPaymentMethodId &&
    paymentCards.some((c) => c.id === defaultPaymentMethodId)
      ? defaultPaymentMethodId
      : paymentCards.length > 0
      ? paymentCards[0].id
      : null);

  const xeroOrgs = orgs.filter((o) => (o.provider ?? "xero") === "xero");
  const qboOrgs = orgs.filter((o) => o.provider === "qbo");

  const handleSubPlanChange = (e) => {
    if (!currentOrg) return;
    if (e?.patch) {
      onPlanChange(currentOrgKey, e.patch);
      return;
    }
    const { name, checked, value } = e.target;
    if (name === "retention_days") {
      onPlanChange(currentOrgKey, { retention_days: Number(value) });
      return;
    }
    if (name === "accounts" && !checked) {
      onPlanChange(currentOrgKey, {
        accounts: false,
        files: false,
        history_and_notes: false,
        payroll: false,
        retention_days: 7,
        skipped: true,
      });
    } else if (name === "accounts" && checked) {
      onPlanChange(currentOrgKey, {
        accounts: true,
        skipped: false,
      });
    } else {
      onPlanChange(currentOrgKey, { [name]: checked });
    }
  };

  const modalWidth = isMulti ? 880 : isQbo ? 560 : 640;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={modalWidth}
      centered
      destroyOnClose
      className="multi-subscribe-wizard-modal max-w-[min(95vw,940px)]"
      styles={{ body: { padding: 0 } }}
    >
      <div className="flex flex-col p-5 sm:p-6 md:p-7 max-h-[88vh] overflow-y-auto">
        <div className="mb-3.5 sm:mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Subscribe</h2>
          <p className="text-xs sm:text-sm text-gray-500">
            {isMulti
              ? "Configure modules for each organization, or skip any you do not want to subscribe."
              : "Configure modules for your organization backup subscription."}
          </p>
        </div>

        {isMulti ? (
          <div className="flex flex-col md:flex-row gap-5 md:gap-6 border-t border-gray-100 pt-4">
            <aside className="w-full md:w-60 lg:w-64 shrink-0 border-b md:border-b-0 md:border-r border-gray-200/80 pb-4 md:pb-0 pr-0 md:pr-5">
              <div className="mb-2 px-1 text-xs">
                <span className="font-semibold uppercase tracking-wider text-gray-400">
                  Organizations ({orgs.length})
                </span>
              </div>

              <div className="space-y-1 max-h-40 md:max-h-[380px] overflow-y-auto">
                {xeroOrgs.length > 0 && qboOrgs.length > 0 && (
                  <p className="px-3 pt-1.5 text-[10px] font-bold uppercase tracking-wider text-sky-600">
                    Xero
                  </p>
                )}
                {xeroOrgs.map((org) => {
                  const key = getOrgKey(org);
                  const plan = orgPlans[key] ?? orgPlans[org.id] ?? getDefaultOrgPlan();
                  const stepIdx = orgs.findIndex((o) => getOrgKey(o) === key);
                  const isActive = !showSummary && stepIdx === currentStepIndex;
                  const status = plan.skipped ? "skipped" : plan.accounts ? "active" : "skipped";
                  return (
                    <OrgSidebarItem
                      key={key}
                      org={org}
                      plan={plan}
                      isActive={isActive}
                      status={status}
                      onSelect={onOrgSelect}
                    />
                  );
                })}

                {xeroOrgs.length > 0 && qboOrgs.length > 0 && (
                  <p className="px-3 pt-2.5 text-[10px] font-bold uppercase tracking-wider text-emerald-600">
                    QuickBooks
                  </p>
                )}
                {qboOrgs.map((org) => {
                  const key = getOrgKey(org);
                  const plan = orgPlans[key] ?? orgPlans[org.id] ?? getDefaultOrgPlan();
                  const stepIdx = orgs.findIndex((o) => getOrgKey(o) === key);
                  const isActive = !showSummary && stepIdx === currentStepIndex;
                  const status = plan.skipped ? "skipped" : plan.accounts ? "active" : "skipped";
                  return (
                    <OrgSidebarItem
                      key={key}
                      org={org}
                      plan={plan}
                      isActive={isActive}
                      status={status}
                      onSelect={onOrgSelect}
                    />
                  );
                })}
              </div>

              <div className="mt-3 border-t border-gray-100 pt-2.5">
                <div
                  onClick={() => onOrgSelect("summary")}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                    showSummary
                      ? "bg-sky-50 text-sky-700"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <CheckCircle className="h-3.5 w-3.5 text-sky-600" />
                  Review & confirm
                </div>
              </div>
            </aside>

            <div className="min-w-0 flex-1 overflow-y-auto">
              {showSummary ? (
                <SummaryStep
                  orgs={orgs}
                  orgPlans={orgPlans}
                  paymentCards={paymentCards}
                  defaultPaymentMethodId={defaultPaymentMethodId}
                  selectedPaymentMethodId={selectedPaymentMethodId}
                  onPaymentMethodChange={onPaymentMethodChange}
                  onAddNewCard={onAddNewCard}
                  onApplyCouponToAll={onApplyCouponToAll}
                  onRemoveCouponFromAll={onRemoveCouponFromAll}
                />
              ) : currentOrg ? (
                <>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <ProviderBadge provider={provider} />
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                      {currentOrg.account}
                    </h3>
                  </div>

                  <SubscribeModulePicker
                    provider={provider}
                    orgCountryCode={currentOrg.country_code}
                    subPlanFormData={currentPlan}
                    onSubPlanChange={handleSubPlanChange}
                    accountsRequired={false}
                    showPricingSummary={currentPlan.accounts && !currentPlan.skipped}
                    chargeImmediately={!!currentOrg.subscribe_will_charge_immediately}
                    introText={
                      currentPlan.skipped || !currentPlan.accounts
                        ? "This organization will be skipped unless you select Account."
                        : null
                    }
                  />

                  {currentPlan.accounts && !currentPlan.skipped && (
                    <div className="mt-4 border-t border-gray-100 pt-3.5">
                      <CouponInput
                        label="Have a Coupon? (This Organization)"
                        helperText="Tip: If you would like to apply one promo code to all organizations at once, you can do so on the Review step."
                        value={currentPlan.coupon_code || ""}
                        onChange={(code) =>
                          onPlanChange(currentOrgKey, { coupon_code: code })
                        }
                        appliedDiscount={currentPlan.coupon_discount}
                        onDiscountApplied={(discountInfo) =>
                          onPlanChange(currentOrgKey, {
                            coupon_discount: discountInfo,
                          })
                        }
                      />
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        ) : (
          /* Single Organization Layout - Clean & Compact without sidebar */
          <div className="border-t border-gray-100 pt-3.5">
            {showSummary ? (
              <SummaryStep
                orgs={orgs}
                orgPlans={orgPlans}
                paymentCards={paymentCards}
                defaultPaymentMethodId={defaultPaymentMethodId}
                selectedPaymentMethodId={selectedPaymentMethodId}
                onPaymentMethodChange={onPaymentMethodChange}
                onAddNewCard={onAddNewCard}
                onApplyCouponToAll={onApplyCouponToAll}
                onRemoveCouponFromAll={onRemoveCouponFromAll}
              />
            ) : currentOrg ? (
              <>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <ProviderBadge provider={provider} />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                    {currentOrg.account}
                  </h3>
                </div>

                <SubscribeModulePicker
                  provider={provider}
                  orgCountryCode={currentOrg.country_code}
                  subPlanFormData={currentPlan}
                  onSubPlanChange={handleSubPlanChange}
                  accountsRequired={false}
                  showPricingSummary={currentPlan.accounts && !currentPlan.skipped}
                  chargeImmediately={!!currentOrg.subscribe_will_charge_immediately}
                  introText={
                    currentPlan.skipped || !currentPlan.accounts
                      ? "This organization will be skipped unless you select Account."
                      : null
                  }
                />

                {currentPlan.accounts && !currentPlan.skipped && (
                  <div className="mt-4 border-t border-gray-100 pt-3.5">
                    <CouponInput
                      value={currentPlan.coupon_code || ""}
                      onChange={(code) =>
                        onPlanChange(currentOrgKey, { coupon_code: code })
                      }
                      appliedDiscount={currentPlan.coupon_discount}
                      onDiscountApplied={(discountInfo) =>
                        onPlanChange(currentOrgKey, {
                          coupon_discount: discountInfo,
                        })
                      }
                    />
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-3.5">
          <button
            type="button"
            onClick={showSummary ? onBack : onClose}
            className="rounded-lg border border-gray-300 px-4 sm:px-6 py-2 text-xs sm:text-sm text-gray-700 transition-colors hover:bg-gray-50"
          >
            {showSummary ? "Back" : "Cancel"}
          </button>

          <div className="flex items-center gap-2.5 sm:gap-3">
            {!showSummary && isMulti && currentStepIndex > 0 && (
              <button
                type="button"
                onClick={() =>
                  onOrgSelect(getOrgKey(orgs[currentStepIndex - 1]))
                }
                className="rounded-lg border border-gray-300 px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50"
              >
                Previous
              </button>
            )}

            {showSummary ? (
              <button
                type="button"
                onClick={() => onConfirm?.(currentPaymentMethodId)}
                disabled={confirming || includedCount === 0}
                className="rounded-lg px-5 sm:px-6 py-2 text-xs sm:text-sm text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: theme.primary }}
              >
                {confirming
                  ? "Processing…"
                  : paymentCards.length === 0
                  ? (isMulti ? "Add Card & Subscribe All" : "Add Card & Subscribe")
                  : "Confirm selection"}
              </button>
            ) : (
              <button
                type="button"
                onClick={onNext}
                className="inline-flex items-center gap-1 rounded-lg px-5 sm:px-6 py-2 text-xs sm:text-sm text-white transition-colors hover:opacity-90"
                style={{ backgroundColor: theme.primary }}
              >
                {isLastOrg ? "Review & Confirm" : "Next"}
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
